import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TwButton, TwButtonType } from '../../../../components/TwButtons/TwButtons';
import { NotificationsContext } from '../../../../providers/Notifications/Notifications.provider';
import useSettings, { SettingsSection } from '../../../../providers/Settings/Settings.provider';
import {
  LoadNotificationRecipients,
  LoadNotificationSettingsRequest,
  LoadNotificationStatusEvents,
  LoadNotificationStopStatusEvents,
  LoadNotificationStopTypes
} from '../../../../providers/Settings/LoadNotificationSettings.model';
import isEqual from 'lodash/isEqual';
import LoadNotificationOptions from './LoadNotificationOptions/LoadNotificationOptions';
import { useUpdateOrdersNotificationSettingsMutation } from '../../../../redux/api/orders/ordersApi';
import TwTagsField from '../../../../components/TwTagsField/TwTagsField';

interface LoadNotificationSettingsValues {
  id?: string;
  order_id?: string | null;
  recipients: LoadNotificationRecipients;
  order_status_events: LoadNotificationStatusEvents;
  stop_status_events: LoadNotificationStopStatusEvents;
  stop_types: LoadNotificationStopTypes;
  use_global_settings?: boolean;
  notify_customer_when_status_changes?: boolean;
}

const LoadNotificationSettings: React.FC = (): JSX.Element => {
  const [originalSettings, setOriginalSettings] = useState<LoadNotificationSettingsValues>({
    id: '',
    order_id: null,
    recipients: {
      customer_contact_email: false,
      customer_billing_email: false,
      user_dispatching_order: false,
      dispatcher_assigned_on_order: false,
      additional_emails: []
    },
    order_status_events: {
      created: false,
      in_transit: false,
      delivered: false
    },
    stop_status_events: {
      in_transit: false,
      arrived: false,
      complete: false
    },
    stop_types: {
      pickup: false,
      drop_off: false,
      transfer: false,
      none: false
    }
  });

  const [discard, setDiscard] = useState<boolean>(false);

  const [loadNotificationSettings, setLoadNotificationSettings] =
    useState<LoadNotificationSettingsValues>({
      id: '',
      order_id: null,
      recipients: {
        customer_contact_email: false,
        customer_billing_email: false,
        user_dispatching_order: false,
        dispatcher_assigned_on_order: false,
        additional_emails: []
      },
      order_status_events: {
        created: false,
        in_transit: false,
        delivered: false
      },
      stop_status_events: {
        in_transit: false,
        arrived: false,
        complete: false
      },
      stop_types: {
        pickup: false,
        drop_off: false,
        transfer: false,
        none: false
      }
    });
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [sendToEmails, setSendToEmails] = useState<string[]>([]);
  const { addNotification } = useContext(NotificationsContext);
  const { updateSettingsTab } = useSettings();
  const [updateOrdersNotificationSettings] = useUpdateOrdersNotificationSettingsMutation();

  /**
   * Discard load notification changes and go back to saved settings
   */
  const handleDiscardChanges = (): void => {
    setDiscard(true);
  };

  /** Update load notification settings */
  const update = (): void => {
    updateOrdersNotificationSettings({ ...loadNotificationSettings, order_id: null })
      .unwrap()
      .then(() => {
        addNotification({
          message: 'Orders notification settings updated successfully',
          variant: 'success'
        });
        setHasChanges(false);
        updateSettingsTab(SettingsSection.appSettings);
      })
      .catch((error: any) => {
        addNotification({
          message: 'Failed to update orders notification settings',
          variant: 'error'
        });
      });
  };

  useEffect(() => {
    if (!isEqual(loadNotificationSettings, originalSettings)) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [loadNotificationSettings, originalSettings]);

  useEffect(() => {
    if (originalSettings.recipients.additional_emails) {
      setSendToEmails(originalSettings.recipients.additional_emails);
    }
  }, [originalSettings.recipients.additional_emails]);

  return (
    <Box>
      <Box className="app-settings__container">
        <Box>
          <Typography variant="h5" className="app-settings__subtitle">
            Order notifications
          </Typography>
          <Typography className="settings__subtitle">
            Manage order status update notifications
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <LoadNotificationOptions
            discardChanges={setDiscard}
            discarded={discard}
            handleOriginalSettings={(orig): void => setOriginalSettings(orig)}
            handleSelectedSettings={(selected): void => setLoadNotificationSettings(selected)}
            isSettingsPage
          />

          <TwTagsField
            id="load-notification-emails"
            label="Additional recipients:"
            tagsDisplayed={sendToEmails}
            tagType={'email'}
            onTagAdded={(emails): void => {
              setSendToEmails(emails);

              const updated = {
                ...loadNotificationSettings,
                recipients: {
                  ...loadNotificationSettings.recipients,
                  additional_emails: emails
                }
              };
              setLoadNotificationSettings(updated);
            }}
          />
          {hasChanges && (
            <div className="account__actions">
              <TwButton
                type={TwButtonType.secondary}
                label="Discard"
                action={handleDiscardChanges}
              />
              <TwButton type={TwButtonType.primary} label="Save changes" action={update} />
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadNotificationSettings;
